<template>
  <ListBuilder
    :allowEmpty="false"
    buttonText="Add URL"
    class="ApplicationDetailsMultiAcs"
    :description="description"
    :items="fields"
    label="ACS URLs"
    :tooltip="tooltip"
    @add="addField"
    @remove="removeField"
  >
    <template #item="{ index, isDefault }">
      <div class="ApplicationDetailsMultiAcs__input">
        <InputText
          aria-label="Index"
          class="ApplicationDetailsMultiAcs__index"
          :focus="false"
          isDisabled
          :label="index <= 1 ? 'Index' : ''"
          :value="index"
        />

        <InputText
          v-model="fields[index].url"
          :aria-label="`ACS URL ${isDefault ? '(Default)' : index}`"
          :autofocus="userInteracted"
          :label="`${isDefault ? 'Default URL' : index === 1 ? 'Additional URLs' : ''}`"
          :name="`ACS URL ${index}`"
          required
          rules="required|url-secure"
          @input="saveConfig"
        />
      </div>
    </template>
  </ListBuilder>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { InputText } from '@jumpcloud/ui-components';
import ListBuilder from 'client/components/ListBuilder.vue';

import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { cloneDeep } from 'lodash';

const ApplicationModel = StoreModelMap[ItemTypeConsts.application];

export default {
  name: 'ApplicationDetailsMultiAcs',

  components: {
    ListBuilder,
    InputText,
  },

  data() {
    return {
      description: 'Enter at least one ACS URL. IdP initiated logins will use the first (default) ACS URL listed. The ACS URL used for SP initiated logins will depend on the authentication request received.',
      fields: [],
      tooltip: 'This is the endpoint where JumpCloud will send SAML responses containing assertions. Enter at least one Assertion Consumer Service (ACS) URL for this service provider. IdP initiated logins will use the first (primary) ACS URL and SP initiated connections will work from any ACS URL included in the list.',
      userInteracted: false,
    };
  },

  computed: {
    ...mapGetters(ApplicationModel, ['item']),

    stringValue() {
      return JSON.stringify(this.fields.map((field) => {
        // Copy item to avoid changing child prop
        const $field = { ...field };

        Object.keys($field).forEach(key => {
          $field[key] = String(field[key]);
        });

        return $field;
      }));
    },
  },

  created() {
    const url = this.item.config?.acsUrl?.value;

    let fields;

    if (url?.includes('[')) {
      fields = JSON.parse(url);

      // Saved object properties are stringified
      // Unstringify index and isDefault
      fields.forEach(field => {
        this.$set(field, 'index', Number(field.index));
        this.$set(field, 'isDefault', field.isDefault === 'true');
      });
    } else {
      fields = [{
        url,
        index: 0,
        isDefault: true,
      }];
    }

    this.$set(this, 'fields', this.fields.concat(fields));
  },

  methods: {
    ...mapActions(ApplicationModel, ['setItemField']),

    addField() {
      // User has interacted, autofocus newly added inputs
      this.userInteracted = true;

      this.$set(this, 'fields', this.fields.concat({
        index: this.fields.length,
        isDefault: false,
        url: 'https://',
      }));

      this.saveConfig();
    },

    removeField(index) {
      const { fields } = this;

      fields.splice(index, 1);

      fields.forEach((item, i) => {
        this.$set(item, 'index', i);
        this.$set(item, 'isDefault', i === 0);
      });

      this.$set(this, 'fields', fields);

      this.saveConfig();
    },

    saveConfig() {
      const config = cloneDeep(this.item.config);

      config.acsUrl.value = this.stringValue;

      this.setItemField({
        key: 'config',
        value: config,
      });
    },
  },
};
</script>

<style scoped>
/*
  Unset align center styles for inputs with a label
  Extra spacing from label causes icon button to not align correctly
*/
.ApplicationDetailsMultiAcs >>> .ListBuilder__item:nth-child(n + 3) .ListBuilder__remove {
  align-self: normal;
}

.ApplicationDetailsMultiAcs__input {
  display: grid;
  grid-column-gap: var(--jc-spacer-small);
  grid-template-columns: auto 1fr;
  grid-template-rows: 1fr;
  margin-bottom: var(--jc-spacer-x-small);
}

/* Unset weirdly nested min-width styles */
.ApplicationDetailsMultiAcs__index >>> [class*="inputWrapper"] {
  min-width: unset;
  width: unset;
}

.ApplicationDetailsMultiAcs__index {
  max-width: var(--jc-size-3p5);
}
</style>
